import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { size } from 'lodash/fp';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Grid, Label, TextField } from '../../common/components';
import messages from '../messages';

const MAX_LENGTH = 30;

const S = {
  PositionedBlock: styled.div`
    position: relative;
  `,
  TitleLength: styled.span`
    position: absolute;
    top: 50%;
    right: 1rem;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
  `,
};

const TitleField = ({
  className, field: { onChange, ...field }, form, intl, ...otherProps
}) => {
  const error = useMemo(() => !!(
    form.errors[field.name] && form.touched[field.name]
  ), [field.name, form.errors, form.touched]);
  const handleChange = useCallback((event) => {
    if (size(event.target.value) <= MAX_LENGTH) {
      onChange(event);
    }
  }, [onChange]);

  return (
    <Grid
      container
      spacing={2}
      alignItems="center"
      className={className}
    >
      <Grid item xs={1}>
        <Label
          htmlFor={field.name}
          error={error}
          required
        >
          <FormattedMessage {...messages.titleFieldLabel} />
        </Label>
      </Grid>
      <Grid item xs={6}>
        <S.PositionedBlock>
          <TextField
            {...field}
            {...otherProps}
            onChange={handleChange}
            id={field.name}
            placeholder={intl.formatMessage(messages.titleFieldPlaceholder)}
            error={error}
            fullWidth
          />
          <S.TitleLength>
            <data value={size(field.value)}>{size(field.value)}</data>
            /30
          </S.TitleLength>
        </S.PositionedBlock>
      </Grid>
    </Grid>
  );
};

TitleField.propTypes = {
  className: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.shape({}),
    ]).isRequired,
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.shape({
      title: PropTypes.string,
    }).isRequired,
    touched: PropTypes.shape({
      title: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

TitleField.defaultProps = {
  className: undefined,
};

export default injectIntl(TitleField);
